import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
    Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { getLatency } from './request/request';
import { LinkHealthData, LinkSnapshot, ROUTER_COLORS } from './Srv6Types';
import InlineSpinner from './InlineSpinner';
import ErrorBanner from './ErrorBanner';
import FreshnessBar from './FreshnessBar';

interface Props {
    selectedSource: string;
    timeRange: string;
}

function formatBw(kbps: number): string {
    if (kbps >= 1e6) return `${(kbps / 1e6).toFixed(0)}G`;
    if (kbps >= 1e3) return `${(kbps / 1e3).toFixed(0)}M`;
    return `${kbps}K`;
}

function shortenIface(name: string): string {
    return name
        .replace('HundredGigE', 'Hu')
        .replace('TenGigE', 'Te')
        .replace('GigabitEthernet', 'Gi')
        .replace('Bundle-Ether', 'BE');
}

type ChartMetric = 'jitter' | 'delay' | 'loss';

const METRIC_CFG: Record<ChartMetric, { label: string; unit: string; color: string }> = {
    jitter: { label: 'Jitter (max-min)', unit: 'us', color: '#f6a400' },
    delay:  { label: 'Avg Delay',        unit: 'us', color: '#00bcd1' },
    loss:   { label: 'Packet Loss',      unit: '%',  color: '#f3494f' },
};

const JitterChart: React.FC<{
    data: LinkHealthData;
    metric: ChartMetric;
    selectedLinks: Set<string>;
}> = ({ data, metric, selectedLinks }) => {
    const { chartData, linkNames } = useMemo(() => {
        const names = selectedLinks.size > 0
            ? Array.from(selectedLinks)
            : data.links.slice(0, 8);

        const cd = data.timeSeries.map((pt) => {
            const row: Record<string, string | number> = {
                time: new Date(pt.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            };
            for (const link of names) {
                const v = pt.links[link];
                if (v) {
                    const metricMap = { loss: v.loss, delay: v.delay, jitter: v.jitter };
                    row[link] = metricMap[metric];
                }
            }
            return row;
        });
        return { chartData: cd, linkNames: names };
    }, [data, metric, selectedLinks]);

    if (chartData.length === 0) return null;
    const cfg = METRIC_CFG[metric];

    return (
        <ResponsiveContainer width="100%" height={340}>
            <LineChart data={chartData} margin={{ top: 4, right: 24, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis
                    tick={{ fontSize: 10 }}
                    width={54}
                    label={{ value: cfg.unit, angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 10 } }}
                />
                {metric === 'loss' && <ReferenceLine y={0} stroke="#ccc" />}
                <RTooltip
                    contentStyle={{ fontSize: 11, borderRadius: 8, maxHeight: 260, overflowY: 'auto' }}
                    formatter={(value: number, name: string) => {
                        const short = name.split('::').map((p, i) => i === 1 ? shortenIface(p) : p).join(' ');
                        return [`${value.toFixed(1)} ${cfg.unit}`, short];
                    }}
                />
                <Legend
                    wrapperStyle={{ fontSize: 9 }}
                    formatter={(value: string) => {
                        const parts = value.split('::');
                        return parts.length === 2 ? `${parts[0]} ${shortenIface(parts[1])}` : value;
                    }}
                />
                {linkNames.map((name, i) => (
                    <Line
                        key={name}
                        type="monotone"
                        dataKey={name}
                        stroke={ROUTER_COLORS[i % ROUTER_COLORS.length]}
                        strokeWidth={1.5}
                        dot={false}
                        connectNulls
                    />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
};

const ALGO_BADGE_COLORS: Record<string, string> = {
    DEFAULT: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    LATENCY: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    TE:      'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

function algoBadgeClass(name: string): string {
    return ALGO_BADGE_COLORS[name.toUpperCase()]
        || 'bg-semantic-bg-secondary text-semantic-text-secondary border-semantic-border-secondary';
}

const SnapshotRow: React.FC<{
    row: LinkSnapshot;
    selected: boolean;
    onToggle: () => void;
}> = ({ row, selected, onToggle }) => {
    let jitterSeverity = 'text-semantic-text-success';
    if (row.jitter > 5000) jitterSeverity = 'text-semantic-text-danger';
    else if (row.jitter > 1000) jitterSeverity = 'text-semantic-text-warning';
    const lossSeverity = row.packetLoss > 0 ? 'text-semantic-text-danger font-bold' : 'text-semantic-text-secondary';

    return (
        <tr
            className={`border-b border-semantic-border-secondary/30 cursor-pointer transition-colors group
                ${selected ? 'bg-semantic-bg-brand-subtle/20' : 'hover:bg-semantic-bg-secondary/40'}`}
            onClick={onToggle}
        >
            <td className="py-1.5 px-2 text-semantic-text-primary">{row.source}</td>
            <td className="py-1.5 px-2 font-mono text-semantic-text-primary text-xs">{shortenIface(row.interface)}</td>
            <td className="py-1.5 px-2">
                {row.neighbor ? (
                    <span className="flex items-center gap-1 text-semantic-text-primary">
                        <svg className="w-3 h-3 text-semantic-text-secondary/60 shrink-0" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M1 8h12M10 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {row.neighbor}
                    </span>
                ) : (
                    <span className="text-semantic-text-secondary/40">&mdash;</span>
                )}
            </td>
            <td className="py-1.5 px-2">
                {row.algos.length > 0 ? (
                    <span className="flex flex-wrap gap-1">
                        {row.algos.map((a) => (
                            <span
                                key={a}
                                className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold border leading-none ${algoBadgeClass(a)}`}
                            >
                                {a}
                            </span>
                        ))}
                    </span>
                ) : (
                    <span className="text-semantic-text-secondary/40">&mdash;</span>
                )}
            </td>
            <td className="text-right py-1.5 px-2 text-semantic-text-primary">{formatBw(row.bandwidthKbps)}</td>
            <td className="text-right py-1.5 px-2 text-semantic-text-primary">{row.delay.toFixed(0)}</td>
            <td className="text-right py-1.5 px-2 text-semantic-text-secondary">{row.minDelay.toFixed(0)}</td>
            <td className="text-right py-1.5 px-2 text-semantic-text-secondary">{row.maxDelay.toFixed(0)}</td>
            <td className={`text-right py-1.5 px-2 font-semibold ${jitterSeverity}`}>{row.jitter.toFixed(0)}</td>
            <td className={`text-right py-1.5 px-2 ${lossSeverity}`}>{row.packetLoss.toFixed(2)}%</td>
        </tr>
    );
};

const Srv6LatencyTab: React.FC<Props> = ({ selectedSource, timeRange }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<LinkHealthData | null>(null);
    const [metric, setMetric] = useState<ChartMetric>('jitter');
    const [selectedLinks, setSelectedLinks] = useState<Set<string>>(new Set());
    const [lastFetched, setLastFetched] = useState<Date | null>(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = await getAccessTokenSilently();
            const res = await getLatency(token, selectedSource || null, timeRange, '3m');
            if (res.data?.data) {
                setData(res.data.data as LinkHealthData);
                setLastFetched(new Date());
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to load link health');
        } finally {
            setIsLoading(false);
        }
    }, [getAccessTokenSilently, selectedSource, timeRange]);

    useEffect(() => { load(); }, [load]);

    const toggleLink = useCallback((source: string, iface: string) => {
        const key = `${source}::${iface}`;
        setSelectedLinks((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    if (isLoading) return <InlineSpinner />;
    if (error) return <ErrorBanner message={error} onRetry={load} />;
    if (!data || data.snapshot.length === 0) {
        return <p className="p-4 text-xs text-semantic-text-secondary">No ISIS interface data for selected range</p>;
    }

    return (
        <div className="flex flex-col gap-4 overflow-auto">
            <FreshnessBar lastFetched={lastFetched} onRefresh={load} />
            {/* Chart panel */}
            <div className="p-4 border rounded-xl border-semantic-border-secondary bg-semantic-bg-primary">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <h2 className="text-sm font-semibold text-semantic-text-primary">Link Health</h2>
                        <div className="flex gap-0.5 p-0.5 bg-semantic-bg-secondary rounded-lg">
                            {(Object.keys(METRIC_CFG) as ChartMetric[]).map((m) => (
                                <button
                                    key={m}
                                    type="button"
                                    onClick={() => setMetric(m)}
                                    className={`px-3 py-1 text-xs rounded-md transition-colors
                                        ${metric === m
                                            ? 'bg-semantic-bg-primary text-semantic-text-primary font-semibold shadow-sm'
                                            : 'text-semantic-text-secondary hover:text-semantic-text-primary'}`}
                                >
                                    {METRIC_CFG[m].label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <span className="text-xs text-semantic-text-secondary">
                        {selectedSource ? `Source: ${selectedSource}` : 'All routers'}
                        {selectedLinks.size > 0 && ` \u00b7 ${selectedLinks.size} selected`}
                    </span>
                </div>
                <JitterChart data={data} metric={metric} selectedLinks={selectedLinks} />
                {selectedLinks.size > 0 && (
                    <button
                        type="button"
                        onClick={() => setSelectedLinks(new Set())}
                        className="mt-2 text-xs text-semantic-text-brand hover:underline"
                    >
                        Clear selection
                    </button>
                )}
            </div>

            {/* Snapshot table */}
            <div className="p-4 border rounded-xl border-semantic-border-secondary bg-semantic-bg-primary overflow-auto">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold text-semantic-text-primary">
                        Per-Interface Snapshot
                    </h3>
                    <span className="text-xs text-semantic-text-secondary">
                        {data.snapshot.length} links &middot; click row to chart
                    </span>
                </div>
                <table className="w-full text-xs border-collapse">
                    <thead>
                        <tr className="border-b border-semantic-border-secondary">
                            <th className="text-left py-1 px-2 font-medium text-semantic-text-secondary">Router</th>
                            <th className="text-left py-1 px-2 font-medium text-semantic-text-secondary">Interface</th>
                            <th className="text-left py-1 px-2 font-medium text-semantic-text-secondary">Neighbor</th>
                            <th className="text-left py-1 px-2 font-medium text-semantic-text-secondary">Flex-Algo</th>
                            <th className="text-right py-1 px-2 font-medium text-semantic-text-secondary">BW</th>
                            <th className="text-right py-1 px-2 font-medium text-semantic-text-secondary">Delay (us)</th>
                            <th className="text-right py-1 px-2 font-medium text-semantic-text-secondary">Min</th>
                            <th className="text-right py-1 px-2 font-medium text-semantic-text-secondary">Max</th>
                            <th className="text-right py-1 px-2 font-medium text-semantic-text-secondary">Jitter</th>
                            <th className="text-right py-1 px-2 font-medium text-semantic-text-secondary">Loss</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.snapshot.map((row) => (
                            <SnapshotRow
                                key={`${row.source}::${row.interface}`}
                                row={row}
                                selected={selectedLinks.has(`${row.source}::${row.interface}`)}
                                onToggle={() => toggleLink(row.source, row.interface)}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Srv6LatencyTab;
